import { SectionHeader } from '@core/components';
import { FEATURE_FLAGS } from '@core/domain';
import { useIsFeatureEnabled } from '@core/hooks';
import { Badge, BadgeSize } from '@soteria/design-system-ve';
import { SortableDataList, type SortableTableColumn } from '@system-config/label-management/components/SortableDataList';
import { ContentType, type StandardLabel } from '@system-config/label-management/domain';
import { useStandardLabels } from '@system-config/label-management/hooks';
import { useMemo } from 'react';

/**
 * Standard Classification Labels section for the Community Context tab.
 * Displays a searchable, sortable, paginated flat list of all standard labels.
 */
const COLUMNS: SortableTableColumn<StandardLabel>[] = [
  {
    key: 'labelName',
    header: 'Label Name',
    render: item => item.labelName,
    width: '70%',
    sortable: true,
    sortKey: 'labelName',
  },
  {
    key: 'contentType',
    header: 'Content Type',
    render: item => <Badge size={BadgeSize.Medium} text={item.contentType?.toUpperCase() || 'TEXT'} />,
    width: '30%',
    sortable: true,
    sortKey: 'contentType',
  },
];

export function StandardLabelsSection() {
  const { labels, loading, errors } = useStandardLabels();
  const isUsernameClassificationEnabled = useIsFeatureEnabled(FEATURE_FLAGS.USERNAME_CLASSIFICATION);

  const filteredLabels = useMemo(
    () => (isUsernameClassificationEnabled ? labels : labels.filter(label => label.contentType !== ContentType.Username)),
    [labels, isUsernameClassificationEnabled]
  );

  return (
    <>
      <SectionHeader
        description="Predefined content classification categories for consistent content management across your communities."
        title={`Standard Labels (${filteredLabels.length})`}
      />
      <SortableDataList
        enablePagination
        searchable
        showFilterBar
        columns={COLUMNS}
        data={filteredLabels}
        dataTypeName="standard label"
        defaultPageSize={10}
        defaultSort={{ key: 'labelName', direction: 'asc' }}
        error={errors}
        isLoading={loading}
        pageSizeOptions={[10, 25, 50, 100]}
        searchKey="labelName"
        searchPlaceholder="Search by label name..."
        sortOptions={[
          { value: 'labelName', label: 'Label Name', sortKey: 'labelName' },
          { value: 'contentType', label: 'Content Type', sortKey: 'contentType' },
        ]}
      />
    </>
  );
}
